import { components } from "@appretio/api";
import { resolveCssVar } from "@appretio/shared";
import { semanticThemeTokens } from "@appretio/ui-kit";
import type {
  AgCartesianChartOptions,
  AgCartesianSeriesOptions
} from "ag-charts-enterprise";
import { useMemo } from "react";

type ReviewDataProduct = components["schemas"]["InSeasonReviewDataProduct"];

export type InSeasonReviewResponseItem = ReviewDataProduct & {
  nis3_sum: number;
  time_purchase_sum: number;
  st_avg: number;
};
export interface UseCategoryChartOptionsProps {
  inSeasonReviewData: InSeasonReviewResponseItem[];
  onCategoryClick?: (category: string) => void;
}

export function useCategoryChartOptions({
  inSeasonReviewData,
  onCategoryClick
}: UseCategoryChartOptionsProps) {
  return useMemo(() => {
    if (!inSeasonReviewData || inSeasonReviewData.length === 0) {
      return {
        series: [] as AgCartesianSeriesOptions[],
        axes: undefined
      };
    }

    const totalNis3 = inSeasonReviewData.reduce(
      (acc, cur) => acc + (cur.nis3_sum || 0),
      0
    );
    const totalPurchase = inSeasonReviewData.reduce(
      (acc, cur) => acc + (cur.time_purchase_sum || 0),
      0
    );

    const chartData = inSeasonReviewData.map((row) => ({
      category: row.product_category || row.style_subcategory || "Unknown",
      sales: totalNis3
        ? parseFloat(((row.nis3_sum / totalNis3) * 100).toFixed(2))
        : 0,
      purchases: totalPurchase
        ? parseFloat(((row.time_purchase_sum / totalPurchase) * 100).toFixed(2))
        : 0,
      sellthrough: parseFloat((row.st_avg * 100).toFixed(2)),

      // Raw values for labels
      salesRaw: row.nis3_sum,
      purchasesRaw: row.time_purchase_sum
    }));

    const series: AgCartesianSeriesOptions[] = [
      {
        type: "bar",
        data: chartData,
        xKey: "category",
        yKey: "sales",
        yName: "Sales (%)",
        stacked: false,
        fill: resolveCssVar(semanticThemeTokens.barChart.salesFill),
        cursor: onCategoryClick ? "pointer" : "default",
        label: {
          enabled: true,
          placement: "outside-end",
          fontSize: 10,
          formatter: ({ datum }) => {
            return `${datum.salesRaw}`;
          }
        }
      },
      {
        type: "bar",
        data: chartData,
        xKey: "category",
        yKey: "purchases",
        yName: "Purchases (%)",
        stacked: false,
        fill: resolveCssVar(semanticThemeTokens.barChart.purchaseFill),
        cursor: onCategoryClick ? "pointer" : "default",
        label: {
          enabled: true,
          placement: "outside-end",
          fontSize: 10,
          formatter: ({ datum }) => {
            return `${datum.purchasesRaw}`;
          }
        }
      },
      // Sellthrough line
      {
        type: "line",
        data: chartData,
        xKey: "category",
        yKey: "sellthrough",
        yName: "Sellthrough (%)",
        stroke: resolveCssVar(semanticThemeTokens.barChart.sellthroughFill),
        cursor: "default",
        marker: {
          enabled: true,
          fill: resolveCssVar(semanticThemeTokens.barChart.sellthroughFill)
        }
      }
    ];

    const axes: AgCartesianChartOptions["axes"] = [
      {
        type: "category",
        position: "bottom",
        paddingInner: 0.2,
        paddingOuter: 0
      },
      {
        type: "number",
        position: "left",
        title: { text: "Sales / Purchases (%)" },
        keys: ["sales", "purchases"],
        label: {
          formatter: ({ value }) => `${value}%`
        },
        min: 0,
        max: 100
      },
      {
        type: "number",
        position: "right",
        title: { text: "Sellthrough (%)" },
        keys: ["sellthrough"],
        label: {
          formatter: ({ value }) => `${value}%`
        },
        min: 0,
        max: 100
      }
    ];

    const listeners = onCategoryClick
      ? {
          seriesNodeClick: (event: { datum?: { category?: string } }) => {
            const category = event?.datum?.category;
            if (category && onCategoryClick) {
              onCategoryClick(category);
            }
          }
        }
      : undefined;

    return { series, axes, listeners };
  }, [inSeasonReviewData, onCategoryClick]);
}
